import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Divider,
  alpha,
  LinearProgress,
  Stack,
  Alert,
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
} from "@mui/material";
import {
  DirectionsCar,
  Save,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Refresh,
  AccessTime,
  Description,
} from "@mui/icons-material";
import MDButton from "shared/components/MDButton";
import MDBox from "shared/components/MDBox";

// Expresión regular para validar placa
// AAA111 (3 letras + 3 números) o AAA11A (3 letras + 2 números + 1 letra)
const PLATE_REGEX = /^[A-Z]{3}\d{3}$|^[A-Z]{3}\d{2}[A-Z]$/;

const CreateCycleDialog = ({
  open,
  onClose,
  onConfirm,
  isSubmitting,
  submitStatus,
  statusMessage,
  terminalInfo = null,
}) => {
  const [formData, setFormData] = useState({
    plate: "",
    date: "",
    observation: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const isInitialized = useRef(false);

  // Función para obtener la fecha y hora actual en formato local
  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Inicializar el formulario SOLO cuando se abre el diálogo y no está inicializado
  useEffect(() => {
    if (open && !isInitialized.current) {
      const currentDateTime = getCurrentDateTime();
      setFormData({
        plate: "",
        date: currentDateTime,
        observation: "",
      });
      setErrors({});
      setTouched({});
      isInitialized.current = true;
    }

    // Resetear el flag cuando se cierra el diálogo
    if (!open) {
      isInitialized.current = false;
    }
  }, [open]);

  const validatePlate = (value) => {
    if (!value || value.trim() === "") {
      return "La placa es requerida";
    }
    const plateUpper = value.toUpperCase().trim();
    if (!PLATE_REGEX.test(plateUpper)) {
      return "Formato inválido. Use AAA111 o AAA11A";
    }
    return "";
  };

  const validateDate = (value) => {
    if (!value) {
      return "La fecha es requerida";
    }
    return "";
  };

  const validateObservation = (value) => {
    if (!value || value.trim() === "") {
      return "La observación es requerida";
    }
    if (value.trim().length < 5) {
      return "La observación debe tener al menos 5 caracteres";
    }
    return "";
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === "plate") {
      processedValue = value.toUpperCase().trim();
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    let error = "";
    if (name === "plate") error = validatePlate(processedValue);
    if (name === "date") error = validateDate(value);
    if (name === "observation") error = validateObservation(value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const validateForm = () => {
    const fields = ["plate", "date", "observation"];
    let isValid = true;

    fields.forEach((field) => {
      let error = "";
      if (field === "plate") error = validatePlate(formData[field]);
      if (field === "date") error = validateDate(formData[field]);
      if (field === "observation") error = validateObservation(formData[field]);
      if (error) {
        isValid = false;
        setErrors((prev) => ({ ...prev, [field]: error }));
        setTouched((prev) => ({ ...prev, [field]: true }));
      }
    });

    return isValid;
  };

  const handleConfirm = () => {
    if (!validateForm()) {
      return;
    }

    // Enviar los datos con la placa en mayúsculas y la fecha en formato local
    onConfirm({
      ...formData,
      plate: formData.plate.toUpperCase().trim(),
    });
  };

  const isFormComplete = () => {
    const requiredFields = ["plate", "date", "observation"];
    return requiredFields.every((field) => formData[field] && formData[field].trim() !== "");
  };

  const isFieldError = (field) => {
    return touched[field] && errors[field];
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          pt: 3,
          px: 3,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box display="flex" alignItems="center">
          {isSubmitting ? (
            <CircularProgress size={28} sx={{ mr: 2, color: "primary.main" }} />
          ) : submitStatus === "success" ? (
            <CheckCircle sx={{ mr: 2, fontSize: "1.75rem", color: "success.main" }} />
          ) : submitStatus === "error" ? (
            <ErrorIcon sx={{ mr: 2, fontSize: "1.75rem", color: "error.main" }} />
          ) : (
            <DirectionsCar sx={{ mr: 2, fontSize: "1.75rem", color: "primary.main" }} />
          )}
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Crear Nuevo Ciclo
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Registre la entrada de un vehículo al parqueadero
            </Typography>
            {terminalInfo && (
              <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                Terminal de entrada: {terminalInfo.hostname || `ID: ${terminalInfo.idterminal}`}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, px: 3 }}>
        <Stack spacing={3}>
          {submitStatus && (
            <Alert
              severity={submitStatus === "success" ? "success" : "error"}
              sx={{ borderRadius: "8px" }}
            >
              <Typography variant="body2" fontWeight="medium">
                {statusMessage}
              </Typography>
            </Alert>
          )}

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Grid container spacing={3}>
              {/* Placa */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Placa *"
                  name="plate"
                  value={formData.plate}
                  onChange={handleFormChange}
                  error={isFieldError("plate")}
                  helperText={isFieldError("plate") && errors.plate}
                  disabled={isSubmitting}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DirectionsCar sx={{ color: "#5c6bc0" }} />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Ej: ABC123 o ABC12A"
                  sx={{
                    "& .MuiInputBase-root": { height: "56px", fontSize: "1rem" },
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  Formato: AAA111 o AAA11A (3 letras + 3 números o 3 letras + 2 números + 1 letra)
                </Typography>
              </Grid>

              {/* Fecha y Hora */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Fecha y Hora de Entrada *"
                  type="datetime-local"
                  name="date"
                  value={formData.date}
                  onChange={handleFormChange}
                  error={isFieldError("date")}
                  helperText={isFieldError("date") && errors.date}
                  disabled={isSubmitting}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTime sx={{ color: "#5c6bc0" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-root": { height: "56px", fontSize: "1rem" },
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  La hora se mantiene en la zona horaria local
                </Typography>
              </Grid>

              {/* Observación */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observación *"
                  name="observation"
                  multiline
                  rows={3}
                  value={formData.observation}
                  onChange={handleFormChange}
                  error={isFieldError("observation")}
                  helperText={isFieldError("observation") && errors.observation}
                  disabled={isSubmitting}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5 }}>
                        <Description sx={{ color: "#5c6bc0" }} />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Describa el motivo de la entrada del vehículo..."
                  sx={{
                    "& .MuiInputBase-root": { fontSize: "1rem" },
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  * La observación es requerida y debe tener al menos 5 caracteres
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Resumen del Ciclo */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid",
              borderColor: alpha("#5c6bc0", 0.3),
              backgroundColor: alpha("#5c6bc0", 0.02),
            }}
          >
            <Typography variant="h6" fontWeight="600" color="text.primary" gutterBottom>
              RESUMEN DEL CICLO
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                >
                  PLACA
                </Typography>
                <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }} noWrap>
                  {formData.plate || "—"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                >
                  TERMINAL DE ENTRADA
                </Typography>
                <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }} noWrap>
                  {terminalInfo ? terminalInfo.hostname || `ID: ${terminalInfo.idterminal}` : "—"}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                >
                  FECHA Y HORA DE ENTRADA
                </Typography>
                <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }}>
                  {formData.date
                    ? new Date(formData.date).toLocaleString("es-ES", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })
                    : "—"}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                >
                  OBSERVACIÓN
                </Typography>
                <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.9rem" }}>
                  {formData.observation || "—"}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Barra de progreso */}
            <Box sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                >
                  COMPLETITUD DEL FORMULARIO
                </Typography>
                <Typography variant="caption" fontWeight="600" sx={{ fontSize: "0.7rem" }}>
                  {Object.values(formData).filter((v) => v && v.toString().trim() !== "").length}
                  /3 campos
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={
                  (Object.values(formData).filter((v) => v && v.toString().trim() !== "").length /
                    3) *
                  100
                }
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: alpha("#5c6bc0", 0.1),
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#5c6bc0",
                    borderRadius: 3,
                  },
                }}
              />
            </Box>

            {isFormComplete() ? (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "8px",
                  backgroundColor: alpha("#4caf50", 0.05),
                  border: "1px dashed",
                  borderColor: alpha("#4caf50", 0.3),
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CheckCircle sx={{ color: "#4caf50", fontSize: 18 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                  Todos los campos requeridos están completos.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "8px",
                  backgroundColor: alpha("#ff9800", 0.05),
                  border: "1px dashed",
                  borderColor: alpha("#ff9800", 0.3),
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Warning sx={{ color: "#ff9800", fontSize: 18 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                  Complete todos los campos requeridos para continuar
                </Typography>
              </Box>
            )}
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, borderTop: 1, borderColor: "divider", gap: 1 }}>
        {submitStatus === "success" ? (
          <MDButton
            variant="contained"
            color="success"
            onClick={onClose}
            startIcon={<CheckCircle />}
            sx={{ px: 4, minWidth: 150, borderRadius: "8px", height: "48px" }}
          >
            Aceptar
          </MDButton>
        ) : submitStatus === "error" ? (
          <>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={onClose}
              sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
            >
              Cerrar
            </MDButton>
            <MDButton
              variant="contained"
              color="warning"
              onClick={() => {
                onClose();
              }}
              startIcon={<Refresh />}
              sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
            >
              Cancelar
            </MDButton>
          </>
        ) : (
          <>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={onClose}
              disabled={isSubmitting}
              sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
            >
              Cancelar
            </MDButton>
            <MDButton
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              disabled={isSubmitting || !isFormComplete()}
              startIcon={<Save />}
              sx={{ px: 4, minWidth: 200, borderRadius: "8px", fontWeight: "bold", height: "48px" }}
            >
              {isSubmitting ? "Creando..." : "CREAR CICLO"}
            </MDButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

CreateCycleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  submitStatus: PropTypes.string,
  statusMessage: PropTypes.string,
  terminalInfo: PropTypes.object,
};

CreateCycleDialog.defaultProps = {
  isSubmitting: false,
  submitStatus: null,
  statusMessage: "",
  terminalInfo: null,
};

export default CreateCycleDialog;
